/**
 * CreateUserModal — modal para crear un propietario o un cliente al vuelo
 * desde el formulario de propiedad. Extraído de propiedades.tsx en round
 * 2026-04-27 (Fase 8) para aislar el sub-flujo de "no encuentro al
 * propietario/cliente en el select → lo creo desde acá mismo".
 *
 * QUÉ HACE
 *   1. Renderiza un modal con un Form distinto (`userForm` interno) según
 *      el target: si target="owner" muestra campos de propietario
 *      (nombre/email/teléfono opcionales); si target="client" muestra
 *      campos de cliente (nombre + email obligatorios + teléfono).
 *   2. Al confirmar: valida campos, llama a `createUsuario`, y si OK,
 *      avisa al padre vía `onCreated(target, newId)` para que conecte el
 *      usuario nuevo al campo correspondiente del form de propiedad
 *      (`propietarioIds` para owner, `idCliente` para client).
 *   3. Cierra y resetea el form al cancelar o tras crear con éxito.
 *
 * POR QUÉ ESTÁ AQUÍ Y NO INLINE
 *   - El bloque inline en propiedades.tsx era ~157 LOC con su propio Form,
 *     handler async de submit, branching por target y validación.
 *   - El submit handler tenía closures sobre `form`, `userForm`, `setOpenUserModal`
 *     — el extract lo aísla detrás de un callback `onCreated(target, id)`.
 *   - Útil para futura reutilización (cualquier otro flujo que necesite
 *     "crear usuario rápido" puede montar este componente).
 *
 * IMPORTANTE — el rol depende del target (round 2026-06-20, fix D1):
 *   El rol "propietario" SÍ existe (sin login, restituido 2026-05-01; ver
 *   docs/auth-roles.md): es el dueño legal de una propiedad. El "cliente"
 *   (con login) es quien la gestiona y ve /finanzas-cliente. Una propiedad
 *   tiene 1 cliente y N propietarios. Por eso:
 *     - target="owner"  → rol "propietario" (manda `nombre_propietario`).
 *     - target="client" → rol "cliente"     (manda `nombre_usuario`).
 *   El backend `/users/upsert` acepta ambos tipos (user.controller.js).
 *   Antes este modal forzaba SIEMPRE "cliente", así que un dueño creado
 *   desde /propiedades caía en la tab CLIENTES — inconsistente con crearlo
 *   desde /clientes (que sí lo deja propietario).
 */

import React from "react";
import { Form, Input, Modal, Typography } from "antd";
import { getModal } from "../../../utils/antdStatic";
import TelefonoInput from "../../../Components/TelefonoInput";
import { reglaTelefono } from "../../../utils/telefonoE164";
import { irAlPrimerError } from "../../../utils/formularioErrores";
import { marcarCampoTelefono } from "../../../utils/telefonoErrorApi";
import type { ErrorApi, OpcionesLlamada } from "../../../services/apiClient";
import type { UsuarioCreate } from "../usePropiedadesLogic";

const { Text } = Typography;

/** Los campos de teléfono de los dos formularios del modal (propietario y cliente). */
const CAMPOS_TELEFONO = ["telefonoPropietario", "telefonoUsuario"];

/** Resultado del creador. `ok=true` trae el id; `ok=false` trae mensaje. */
type CreateResult =
  | { ok: true; id: number }
  | { ok: false; message: string };

interface CreateUserModalProps {
  /** Visibilidad del modal (controlado por el padre). */
  open: boolean;
  /**
   * Determina qué grupo de campos se renderiza:
   *   - "owner"  → propietario (nombre obligatorio, resto opcional).
   *   - "client" → cliente (nombre + email obligatorios — el email se usa
   *                para enviar welcome y recuperación de contraseña).
   */
  target: "owner" | "client";
  /**
   * Callback async del padre que ejecuta el alta real (POST /users/upsert).
   * Devolvemos `CreateResult` discriminado para que el modal sepa si cerrar
   * o mostrar error sin acoplarse al transporte.
   */
  createUsuario: (payload: UsuarioCreate, opciones?: OpcionesLlamada) => Promise<CreateResult>;
  /** Llamado al cancelar o al cerrar el modal. El padre resetea su estado. */
  onCancel: () => void;
  /**
   * Llamado tras creación exitosa, ANTES de cerrar. El padre lo usa para
   * vincular el usuario nuevo al form de propiedad (`propietarioIds` o
   * `idCliente`). El modal se cierra automáticamente después.
   */
  onCreated: (target: "owner" | "client", newId: number) => void;
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({
  open,
  target,
  createUsuario,
  onCancel,
  onCreated,
}) => {
  // Form interno propio del modal — distinto al form de la propiedad. Lo
  // creamos acá (no recibido por prop) porque su lifecycle está atado al
  // modal: al cancelar/crear lo reseteamos sin tocar el form padre.
  const [userForm] = Form.useForm();

  /**
   * Handler del OK: valida, llama al creador del padre, y si OK avisa
   * para vincular el id resultante al form de propiedad. En error de
   * validación muestra modal con la lista de errores; en error de creación
   * muestra modal con el mensaje del backend.
   */
  const handleOk = async () => {
    let values: any;
    try {
      values = await userForm.validateFields();
    } catch (err: any) {
      // AntD Form.validateFields rechaza con `{ errorFields: [{name,errors}]}`.
      // Mostramos el primer mensaje de cada campo en una lista, no
      // intentamos serializar el shape entero (sería ruidoso).
      const errorFields: {
        name: (string | number)[];
        errors: string[];
      }[] = err?.errorFields ?? [];
      if (errorFields.length) {
        const esDeTelefono = (f: { name: (string | number)[] }) => CAMPOS_TELEFONO.includes(String(f.name[0]));
        if (errorFields.every(esDeTelefono)) {
          // Lo único que falla es el teléfono: NO falta, está mal escrito, y el motivo ya está bajo el campo. Un diálogo titulado
          // «Faltan campos obligatorios» con «El teléfono solo puede tener números…» confundía (ronda F3, H5). Basta llevar a la
          // persona al campo, darle el foco y el aviso breve de siempre.
          irAlPrimerError(userForm, err);
          return;
        }
        // Además del diálogo, la pantalla va al primer campo con error y le da el foco (sin aviso aparte: el diálogo ya es el aviso).
        irAlPrimerError(userForm, err, { avisar: false });
        getModal()?.error({
          // Mezcla de faltantes y de un teléfono mal escrito: el título no puede decir que todo «falta».
          title: errorFields.some(esDeTelefono) ? "Revisa los campos marcados" : "Faltan campos obligatorios",
          width: 480,
          content: (
            <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
              {errorFields.map((f, i) => (
                <li key={i}>{f.errors[0]}</li>
              ))}
            </ul>
          ),
        });
      }
      return;
    }

    // Fix D1 (2026-06-20): el rol depende del target. El dueño legal va con
    // rol "propietario" (sin login); el gestor con rol "cliente" (con login).
    // El contrato /users/upsert acepta ambos y diferencia las columnas
    // (`nombre_propietario` vs `nombre_usuario`). Ver docs/auth-roles.md.
    const rol = target === "owner" ? "propietario" : "cliente";

    const payload: UsuarioCreate = {
      nombre_propietario: values.nombrePropietario ?? null,
      email_propietario: values.emailPropietario ?? null,
      telefono_propietario: values.telefonoPropietario || null,

      nombre_usuario: values.nombreUsuario ?? null,
      email_usuario: values.emailUsuario ?? null,
      telefono_usuario: values.telefonoUsuario || null,

      rol,
    };

    // Si el servidor rechaza el teléfono (409 duplicado, 400 formato) se marca junto al campo, con el foco, además del aviso global
    // (ronda F3, H7). El servidor no dice qué campo fue: en este modal hay uno solo, el del teléfono del target.
    let telefonoMarcado = false;
    const alFallar = (error: ErrorApi) => {
      if (marcarCampoTelefono(userForm, target === "owner" ? "telefonoPropietario" : "telefonoUsuario", error)) telefonoMarcado = true;
    };
    const result = await createUsuario(payload, { alFallar });

    if (!result.ok) {
      // Un rechazo por el teléfono ya quedó explicado bajo el campo: el diálogo genérico («Verifica que el email no esté ya
      // registrado») hablaría de otra cosa.
      if (telefonoMarcado) return;
      // Solo mostramos el modal de error si el backend devolvió mensaje
      // concreto — apiFetch ya muestra toast genérico cuando falla, no
      // duplicamos UX.
      if (result.message) {
        getModal()?.error({
          title: "No se pudo crear el usuario",
          content: result.message,
        });
      }
      return;
    }

    // Avisa al padre para que vincule (`propietarioIds += newId` o
    // `idCliente = newId` según target), luego limpia y cierra.
    onCreated(target, result.id);
    onCancel();
    userForm.resetFields();
  };

  /**
   * Cancelación explícita o cierre por outside-click: limpiamos el form
   * para que el siguiente abrir arranque vacío. `destroyOnClose` del
   * Modal también ayuda pero el reset es defensivo.
   */
  const handleCancel = () => {
    userForm.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={
        target === "owner" ? "Crear nuevo propietario" : "Crear nuevo cliente"
      }
      open={open}
      onCancel={handleCancel}
      onOk={handleOk}
      destroyOnClose
    >
      <Form form={userForm} layout="vertical" preserve={false}>
        <div className="flex-container">
          {target === "owner" ? (
            // ── Owner: solo nombre obligatorio, resto opcional ──────
            // Los propietarios no necesariamente tienen email/teléfono
            // — algunos son personas físicas que el admin solo necesita
            // referenciar en el catastro de propiedad.
            <OwnerFields />
          ) : (
            // ── Client: nombre + email obligatorios ─────────────────
            // El cliente SÍ recibe welcome email + recuperación de
            // contraseña, por eso el email es obligatorio.
            <ClientFields />
          )}
        </div>

        <Text type="secondary">
          El rol se asigna automáticamente como{" "}
          {target === "owner" ? "propietario" : "cliente"}.
        </Text>
      </Form>
    </Modal>
  );
};

// ─── Sub-componentes de campos ──────────────────────────────────────────
//
// Separados como componentes internos (no exportados) para que el switch
// owner/client del render principal quede limpio.

const OwnerFields: React.FC = () => (
  <>
    <Form.Item
      label="Nombre"
      name="nombrePropietario"
      // El mensaje dice DE QUÉ campo habla: el diálogo de validación lista solo estos textos, y un «Requerido» suelto no decía cuál
      // faltaba (ronda F3b). Igual que en el formulario del cliente, que ya nombra su campo.
      rules={[{ required: true, message: "El nombre del propietario es obligatorio" }]}
    >
      <Input />
    </Form.Item>
    <Form.Item label="Email" name="emailPropietario">
      <Input />
    </Form.Item>
    <Form.Item label="Teléfono" name="telefonoPropietario" rules={[reglaTelefono()]}>
      {/*
       * Teléfono con código de país (el campo compone el valor canónico +56912345678 mientras se teclea y deja pegar un número
       * completo). Opcional: vacío es válido; si se escribe, tiene que estar completo.
       */}
      <TelefonoInput ariaLabel="Teléfono del propietario" />
    </Form.Item>
  </>
);

const ClientFields: React.FC = () => (
  <>
    <Form.Item
      label="Nombre"
      name="nombreUsuario"
      rules={[
        { required: true, message: "El nombre del cliente es obligatorio" },
      ]}
    >
      <Input />
    </Form.Item>
    <Form.Item
      label="Email"
      name="emailUsuario"
      rules={[
        {
          required: true,
          message:
            "El email es obligatorio (se usa para welcome y recuperación de contraseña)",
        },
        { type: "email", message: "Formato de email inválido" },
      ]}
    >
      <Input placeholder="nombre@dominio.com" />
    </Form.Item>
    <Form.Item label="Teléfono" name="telefonoUsuario" rules={[reglaTelefono()]}>
      <TelefonoInput ariaLabel="Teléfono del cliente" />
    </Form.Item>
  </>
);

export default CreateUserModal;
